import type { AnalyticsEventInput } from "../integrations/api";
import type { AnalyticsSession } from "./events";
import { analyticsClient, createAnalyticsEvent } from "./events";
import { consentManager } from "../privacy/consent";

const STORAGE_KEY = "conan.quiz.pending-pageview.v1";
const ENQUEUED_STORAGE_KEY = "conan.quiz.pending-pageview-enqueued.v1";

type FunnelVariant = "A" | "B";

interface StoredPageView {
  variant: FunnelVariant;
  event: AnalyticsEventInput;
}

interface StoredEnqueueReceipt {
  eventId: string;
  sessionId: string;
}

function storage(): Storage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

export function getPendingFunnelPageView(variant: FunnelVariant): AnalyticsEventInput | undefined {
  try {
    const raw = storage()?.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as StoredPageView;
    if (parsed.variant !== variant || !parsed.event) return undefined;
    return createAnalyticsEvent("FunnelPageView", {
      eventId: parsed.event.eventId,
      ...(parsed.event.occurredAt ? { occurredAt: parsed.event.occurredAt } : {}),
      ...(parsed.event.pagePath ? { pagePath: parsed.event.pagePath } : {}),
      ...(parsed.event.metadata ? { metadata: parsed.event.metadata } : {}),
    });
  } catch {
    return undefined;
  }
}

/** Records the landing view before a server session exists. */
export function recordPendingFunnelPageView(
  variant: FunnelVariant,
  pagePath: string,
): AnalyticsEventInput {
  const event = createAnalyticsEvent("FunnelPageView", {
    pagePath,
    metadata: { variant },
  });
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify({ variant, event } satisfies StoredPageView));
  } catch {
    // Provider-side measurement still works when session storage is unavailable.
  }
  return event;
}

/**
 * Attaches the pre-session landing view to the newly authenticated funnel
 * session using the exact same event ID used by browser providers.
 */
export function enqueuePendingFunnelPageView(
  variant: FunnelVariant,
  session: AnalyticsSession,
): boolean {
  if (!consentManager.allowsAnalytics()) return false;
  const event = getPendingFunnelPageView(variant);
  if (!event) return false;
  try {
    const rawReceipt = storage()?.getItem(ENQUEUED_STORAGE_KEY);
    if (rawReceipt) {
      const receipt = JSON.parse(rawReceipt) as StoredEnqueueReceipt;
      if (receipt.eventId === event.eventId && receipt.sessionId === session.sessionId) return false;
    }
  } catch {
    // The event queue also deduplicates the same event ID in memory.
  }
  analyticsClient.setSession(session);
  analyticsClient.queue.enqueue(event);
  try {
    storage()?.setItem(ENQUEUED_STORAGE_KEY, JSON.stringify({
      eventId: event.eventId,
      sessionId: session.sessionId,
    } satisfies StoredEnqueueReceipt));
  } catch {
    // Queue/server event-ID deduplication keeps a repeated enqueue safe.
  }
  void analyticsClient.flush();
  return true;
}
